/**
 * Client-side PDF generation for the Übergabeprotokoll.
 *
 * Rendered from the tenant Done step and from the admin protocol detail view,
 * so both sides download byte-identical documents.
 *
 * ── Why this is hand-drawn rather than autoTable ─────────────────────────────
 * The previous version composed the page out of `jspdf-autotable` calls and a
 * shared `ensureSpace()` closure. Two things went wrong with that, both visible
 * in a real three-page protocol:
 *
 *  1. `renderPhotoGrid` kept its OWN `let y`, but called the caller's
 *     `ensureSpace()` — which mutated the caller's `y` and added the page.
 *     After a break the grid carried on drawing at its stale coordinate, so
 *     photos landed off the bottom of the new sheet and over the footer.
 *     Here a single painter owns `y`; there is no second copy to go stale.
 *  2. `ensureSpace(40)` reserved room for a heading only, never for the
 *     heading plus the first row it introduces, which orphaned every section
 *     title at the foot of a page. Reservations are now measured from the
 *     block that follows.
 *
 * Hand-drawing also buys exact control over the two things autoTable got
 * wrong: column widths wide enough that "Gebrauchsspuren" does not break
 * mid-word, and a photo column carrying the photo's reference code instead of
 * a "✓" — which jsPDF's WinAnsi Helvetica cannot encode, and printed as an
 * apostrophe.
 *
 * ── Typography ───────────────────────────────────────────────────────────────
 * The design calls for IBM Plex; this stays on Helvetica with `charSpace`
 * tracking on the uppercase labels. Embedding two Plex faces would add
 * ~200 KB to the client bundle for a document a tenant opens once, and jsPDF
 * has no subsetter. The tracking is what carries the look.
 */

import jsPDF from "jspdf";
import {
  COLIVIQ_MARK_PNG_DATA_URL,
  coliviqMarkMetrics,
} from "@/lib/brand-mark";
import { COMMON_AREAS, isAdminRecorded, isAutoSubmitted } from "@/lib/protocol";
import { compressImageBlob } from "@/lib/image-compress";
import { getSignedUrl } from "@/hooks/useProtocol";
import type {
  Protocol,
  ProtocolDefect,
  ProtocolInventoryItem,
  ProtocolPhoto,
  ItemCondition,
  Severity,
  PhotoCategory,
  CommonArea,
} from "@/lib/protocol";

export type ProtocolPdfInput = {
  protocol: Protocol;
  photos: ProtocolPhoto[];
  inventory: ProtocolInventoryItem[];
  defects: ProtocolDefect[];
  booking: {
    property_name: string | null;
    room_number: string | null;
    address: string | null;
    /** Optional: renders the tenancy strip. Omitted cells are dropped. */
    move_in_date?: string | null;
    move_out_date?: string | null;
    deposit_amount?: number | null;
  } | null;
  tenant: {
    name: string | null;
    email: string | null;
  };
  language?: "en" | "de";
};

/* ── the page, in points ─────────────────────────────────────────────────── */

const A4 = { w: 595.28, h: 841.89 } as const;
const MARGIN = 48;
const CONTENT_W = A4.w - MARGIN * 2;
const RIGHT = A4.w - MARGIN;
const TOP_BAR_H = 5.6;
const BOT_BAR_H = 9.1;
const BODY_TOP = TOP_BAR_H + 22;
/** Everything above the footer rule. Measured, not guessed. */
const CONTENT_BOTTOM = A4.h - BOT_BAR_H - 40;

const GAP_SECTION = 22;
const GAP_BLOCK = 14;
const GAP_HEAD = 9;
const GAP_ROW = 6;
const GUTTER = 10;

/* ── palette, sampled from the operator's letterhead ─────────────────────── */

type RGB = readonly [number, number, number];
const PAPER: RGB = [250, 249, 247];
const WHITE: RGB = [255, 255, 255];
const INK: RGB = [16, 30, 29];
const MUTED: RGB = [98, 113, 111];
const FAINT: RGB = [141, 155, 153];
const RULE: RGB = [226, 224, 218];
const RULE_SOFT: RGB = [237, 235, 229];
/** Current coliviq primary brand colour (#092E2C). */
const TEAL: RGB = [9, 46, 44];
const TEAL_DEEP: RGB = [9, 46, 44];
const BAR: RGB = [27, 42, 41];
const GOOD: RGB = [15, 123, 90];
const GOOD_BG: RGB = [234, 246, 241];
const WEAR: RGB = [152, 89, 10];
const WEAR_BG: RGB = [251, 242, 228];
const BAD: RGB = [168, 50, 31];
const BAD_BG: RGB = [250, 237, 234];

const COMPANY = {
  name: "Coliviq GmbH",
  tagline: "Stylish Living  ·  Smart Renting",
  address: "Elvirastraße 10 · 80636 München",
  contact: "+49 8137 6059818 · kontakt@coliviq.de",
  legal:
    "Coliviq GmbH    |    HRB 307671 · AG München    |    " +
    "USt-IdNr. DE459890336    |    StNr. 143/125/50416",
} as const;

/* ── labels ──────────────────────────────────────────────────────────────── */

const COND_LABEL: Record<ItemCondition, { en: string; de: string }> = {
  good: { en: "Good", de: "Gut" },
  wear: { en: "Signs of wear", de: "Gebrauchsspuren" },
  damaged: { en: "Damaged", de: "Beschädigt" },
};

const COND_COLOR: Record<ItemCondition, { fg: RGB; bg: RGB }> = {
  good: { fg: GOOD, bg: GOOD_BG },
  wear: { fg: WEAR, bg: WEAR_BG },
  damaged: { fg: BAD, bg: BAD_BG },
};

const SEV_LABEL: Record<Severity, { en: string; de: string }> = {
  low: { en: "Low", de: "Gering" },
  medium: { en: "Medium", de: "Mittel" },
  high: { en: "Serious", de: "Schwerwiegend" },
};

const AREA_LABEL: Record<CommonArea, { en: string; de: string }> = {
  kitchen: { en: "Kitchen", de: "Küche" },
  bath: { en: "Bathroom", de: "Bad" },
  hallway: { en: "Hallway", de: "Flur" },
  other: { en: "Other", de: "Sonstiges" },
};

/**
 * Photo reference prefixes.
 *
 * Printed beside every plate and in the inventory + defect tables, so a reader
 * looking at "Foto B-02" in a defect row can find the plate it refers to. The
 * codes are deliberately language-neutral (they are codes, not words).
 */
const PHOTO_PREFIX: Record<PhotoCategory, string> = {
  room: "Z",
  keys: "KEY",
  kitchen: "K",
  bath: "B",
  hallway: "F",
  other: "S",
};
const INVENTORY_PREFIX = "I";

/* ── image plumbing (unchanged behaviour) ────────────────────────────────── */

/**
 * Fetches an image (storage path) as a data URL — needed by jsPDF.addImage.
 *
 * Photos are downscaled and re-encoded on the way through: they are drawn into
 * a ~160 x 120pt box, so a 4000px phone original contributes megabytes nobody
 * can see and makes a photo-heavy protocol too large to email. Compression
 * degrades to the original bytes on any failure — see lib/image-compress.
 */
async function pathToDataUrl(path: string): Promise<string | null> {
  const url = await getSignedUrl(path);
  if (!url) return null;
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const blob = await compressImageBlob(await res.blob());
    return await new Promise<string | null>((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve((reader.result as string) || null);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

function getImageFormat(dataUrl: string): "JPEG" | "PNG" {
  return dataUrl.startsWith("data:image/png") ? "PNG" : "JPEG";
}

/**
 * Dates and times are rendered in Europe/Berlin, not the viewer's zone.
 *
 * This document is a German tenancy record: the timestamp on it is the one the
 * operator and the tenant will both quote, and it has to say the same thing
 * whoever downloads it. Without the pinned zone a protocol filed at 21:42 in
 * Berlin printed as "01:12" for a reader in another zone — and a date-only
 * value could land on the wrong calendar day west of UTC.
 */
const BERLIN = "Europe/Berlin";

function formatDateDE(iso: string | Date | null | undefined): string {
  if (!iso) return "—";
  const d = typeof iso === "string" ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("de-DE", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: BERLIN,
  });
}

function formatTimeDE(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("de-DE", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: BERLIN,
  });
}

function formatEuro(amount: number): string {
  return `${amount.toLocaleString("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} €`;
}

/* ── the painter ─────────────────────────────────────────────────────────── */

type Cell = { k: string; v: string; n?: string };
type Row = { label: string; value: string; sub?: string };

/**
 * Owns the cursor.
 *
 * Every block advances `this.y`, and every block that could not fit calls
 * `need()` FIRST — which may start a page and reset `y`. Because there is
 * exactly one `y` in the whole file, the stale-cursor class of bug that put
 * photos through the footer cannot come back.
 */
class Painter {
  y = BODY_TOP;

  constructor(
    private doc: jsPDF,
    private lang: "de" | "en"
  ) {
    this.paintPageFurniture();
  }

  private t(en: string, de: string): string {
    return this.lang === "de" ? de : en;
  }

  /** Paper ground + teal top rule. Runs for every page, before its content. */
  private paintPageFurniture(): void {
    const d = this.doc;
    d.setFillColor(...PAPER);
    d.rect(0, 0, A4.w, A4.h, "F");
    d.setFillColor(...TEAL);
    d.rect(0, 0, A4.w, TOP_BAR_H, "F");
  }

  /**
   * Identifies continuation pages once the letterhead is behind us.
   *
   * A loose page 2 of a tenancy record that names neither the tenancy nor the
   * document is not evidence of anything, so every page after the first opens
   * with the mark and the reference.
   */
  private continuationMeta: string | null = null;

  setContinuationMeta(meta: string): void {
    this.continuationMeta = meta;
  }

  newPage(): void {
    this.doc.addPage();
    this.paintPageFurniture();
    this.y = BODY_TOP;
    if (this.continuationMeta) this.runningHead(this.continuationMeta);
  }

  /** Reserve `h` points; break first if they are not there. */
  need(h: number): void {
    if (this.y + h > CONTENT_BOTTOM) this.newPage();
  }

  /* ── primitives ───────────────────────────────────────────────────────── */

  private font(size: number, weight: "normal" | "bold", color: RGB): void {
    this.doc.setFont("helvetica", weight);
    this.doc.setFontSize(size);
    this.doc.setTextColor(color[0], color[1], color[2]);
  }

  /** Plain text, anchored by its TOP edge so every y in this file agrees. */
  private text(
    s: string,
    x: number,
    y: number,
    size: number,
    weight: "normal" | "bold",
    color: RGB,
    opts: { align?: "left" | "right"; maxWidth?: number } = {}
  ): void {
    this.font(size, weight, color);
    this.doc.text(s, x, y, {
      baseline: "top",
      align: opts.align ?? "left",
      ...(opts.maxWidth ? { maxWidth: opts.maxWidth } : {}),
    });
  }

  /** Width of tracked text — jsPDF's getTextWidth ignores charSpace. */
  private trackedWidth(s: string, size: number, track: number): number {
    this.doc.setFontSize(size);
    return this.doc.getTextWidth(s) + track * Math.max(0, s.length - 1);
  }

  /**
   * Letter-spaced uppercase label — the workhorse of the layout.
   *
   * Right alignment is done by measurement rather than by jsPDF's `align`,
   * which does not account for `charSpace` and drifts a few points per label.
   */
  private label(
    s: string,
    x: number,
    y: number,
    opts: {
      size?: number;
      color?: RGB;
      weight?: "normal" | "bold";
      track?: number;
      align?: "left" | "right";
    } = {}
  ): number {
    const size = opts.size ?? 6.8;
    const track = opts.track ?? size * 0.14;
    const upper = s.toUpperCase();
    const w = this.trackedWidth(upper, size, track);
    this.font(size, opts.weight ?? "normal", opts.color ?? FAINT);
    this.doc.text(upper, opts.align === "right" ? x - w : x, y, {
      baseline: "top",
      charSpace: track,
    });
    return w;
  }

  private rule(y: number, x1 = MARGIN, x2 = RIGHT, color: RGB = RULE): void {
    this.doc.setDrawColor(color[0], color[1], color[2]);
    this.doc.setLineWidth(0.8);
    this.doc.line(x1, y, x2, y);
  }

  private box(
    x: number,
    y: number,
    w: number,
    h: number,
    fill: RGB | null,
    border: RGB | null
  ): void {
    const d = this.doc;
    if (fill) d.setFillColor(...fill);
    if (border) {
      d.setDrawColor(border[0], border[1], border[2]);
      d.setLineWidth(0.9);
    }
    const style = fill && border ? "FD" : fill ? "F" : "S";
    d.rect(x, y, w, h, style);
  }

  /** Wrapped paragraph. Returns the height it consumed. */
  private paragraph(
    s: string,
    x: number,
    y: number,
    w: number,
    size: number,
    color: RGB,
    weight: "normal" | "bold" = "normal"
  ): number {
    this.font(size, weight, color);
    const lines = this.doc.splitTextToSize(s, w) as string[];
    const lh = size * 1.36;
    lines.forEach((line, i) => {
      this.doc.text(line, x, y + i * lh, { baseline: "top" });
    });
    return lines.length * lh;
  }

  private measure(s: string, w: number, size: number): number {
    this.doc.setFont("helvetica", "normal");
    this.doc.setFontSize(size);
    const lines = this.doc.splitTextToSize(s, w) as string[];
    return lines.length * size * 1.36;
  }

  /** Condition / severity chip. Returns its width. */
  private chip(s: string, x: number, y: number, fg: RGB, bg: RGB): number {
    const size = 6.8;
    const track = 0.55;
    const padX = 5.5;
    const padY = 2.4;
    const upper = s.toUpperCase();
    const tw = this.trackedWidth(upper, size, track);
    const w = tw + padX * 2;
    const h = size + padY * 2 + 1;
    this.doc.setFillColor(...bg);
    this.doc.roundedRect(x, y, w, h, 1.5, 1.5, "F");
    this.font(size, "bold", fg);
    this.doc.text(upper, x + padX, y + padY + 0.5, {
      baseline: "top",
      charSpace: track,
    });
    return w;
  }

  /* ── blocks ───────────────────────────────────────────────────────────── */

  letterhead(): void {
    const mark = coliviqMarkMetrics(16);
    this.doc.addImage(
      COLIVIQ_MARK_PNG_DATA_URL,
      "PNG",
      MARGIN,
      this.y,
      mark.width,
      mark.height
    );
    this.label(COMPANY.tagline, MARGIN, this.y + mark.height + 5, {
      size: 7,
      color: TEAL_DEEP,
      track: 1.4,
    });

    this.text(COMPANY.name, RIGHT, this.y, 9, "bold", INK, { align: "right" });
    this.text(COMPANY.address, RIGHT, this.y + 12, 8, "normal", MUTED, {
      align: "right",
    });
    this.text(COMPANY.contact, RIGHT, this.y + 23, 8, "normal", MUTED, {
      align: "right",
    });

    this.y += mark.height + 16;
    this.rule(this.y);
    this.y += GAP_SECTION;
  }

  /** Compact letterhead for continuation pages. */
  runningHead(meta: string): void {
    const mark = coliviqMarkMetrics(10);
    this.doc.addImage(
      COLIVIQ_MARK_PNG_DATA_URL,
      "PNG",
      MARGIN,
      this.y,
      mark.width,
      mark.height
    );
    this.label(meta, RIGHT, this.y + 3, {
      size: 7,
      color: MUTED,
      align: "right",
      track: 0.7,
    });
    this.y += mark.height + 12;
    this.rule(this.y);
    this.y += GAP_BLOCK;
  }

  titleBlock(o: {
    kicker: string;
    title: string;
    sub: string;
    refLabel: string;
    ref: string;
    status: string;
    statusColor: { fg: RGB; bg: RGB };
    when: string;
  }): void {
    const stampW = 200;
    const stampX = RIGHT - stampW;
    const stampTop = this.y;

    // Left: kicker → title → subtitle. The subtitle is capped at the stamp's
    // left edge; unconstrained, a longer status ("Frist abgelaufen, Zimmer gilt
    // als angenommen") ran straight under the stamp box.
    const leftW = stampX - MARGIN - 16;
    this.label(o.kicker, MARGIN, this.y, { size: 7.2, color: MUTED });
    this.text(o.title, MARGIN, this.y + 14, 25, "bold", INK);
    const subH = this.paragraph(
      o.sub,
      MARGIN,
      this.y + 46,
      leftW,
      10,
      TEAL_DEEP,
      "bold"
    );

    // Right: the reference stamp — white card, teal rail, status pill.
    const stampH = 62;
    this.box(stampX, stampTop, stampW, stampH, WHITE, RULE);
    this.doc.setFillColor(...TEAL);
    this.doc.rect(stampX, stampTop, 2.6, stampH, "F");
    this.label(o.refLabel, stampX + 12, stampTop + 8, { size: 6.6 });
    this.label(o.ref, stampX + 12, stampTop + 19, {
      size: 13.5,
      color: INK,
      weight: "bold",
      track: 0.6,
    });
    const pillW = this.chip(
      o.status,
      stampX + 12,
      stampTop + 37,
      o.statusColor.fg,
      o.statusColor.bg
    );
    void pillW;
    this.text(o.when, stampX + 12, stampTop + 51, 7.6, "normal", MUTED);

    // Whichever column is taller sets the floor.
    this.y = Math.max(stampTop + stampH, stampTop + 46 + subH) + GAP_SECTION;
  }

  /**
   * Section heading.
   *
   * `reserve` is the height of the FIRST block the section introduces, not of
   * the heading. Reserving only the heading is what stranded
   * "Inventar-Fotos" alone at the foot of a page — and, with no reservation at
   * all, printed it straight over the footer.
   */
  sectionHead(de: string, en: string, reserve = 60): void {
    this.need(GAP_HEAD + 5 + reserve);
    const w = this.label(this.lang === "de" ? de : en, MARGIN, this.y, {
      size: 7.6,
      color: INK,
      weight: "bold",
    });
    const secondary = this.lang === "de" ? en : de;
    const w2 = this.label(secondary, MARGIN + w + 8, this.y, {
      size: 7.6,
      color: FAINT,
    });
    this.rule(this.y + 4, MARGIN + w + 8 + w2 + 8, RIGHT);
    this.y += GAP_HEAD + 5;
  }

  /** Two side-by-side definition lists. */
  twoColumnFacts(left: Row[], right: Row[]): void {
    const colW = (CONTENT_W - 22) / 2;
    const labelW = 74;
    const rowsH = (rows: Row[]) =>
      rows.reduce((h, r) => h + (r.sub ? 25 : 15), 0);
    this.need(Math.max(rowsH(left), rowsH(right)));

    const draw = (rows: Row[], x: number) => {
      let cy = this.y;
      for (const r of rows) {
        this.label(r.label, x, cy + 1.5, { size: 6.9, track: 0.6 });
        this.text(r.value, x + labelW, cy, 9.4, "bold", INK, {
          maxWidth: colW - labelW,
        });
        if (r.sub) {
          this.text(r.sub, x + labelW, cy + 11, 8, "normal", MUTED, {
            maxWidth: colW - labelW,
          });
        }
        cy += r.sub ? 25 : 15;
      }
      return cy;
    };
    const endL = draw(left, MARGIN);
    const endR = draw(right, MARGIN + colW + 22);
    this.y = Math.max(endL, endR) + GAP_SECTION;
  }

  /** The bordered data strip. Drops nothing — the caller filters. */
  strip(cells: Cell[]): void {
    if (!cells.length) return;
    const h = 44;
    this.need(h);
    const cw = CONTENT_W / cells.length;
    this.box(MARGIN, this.y, CONTENT_W, h, WHITE, RULE);
    cells.forEach((c, i) => {
      const x = MARGIN + i * cw;
      if (i > 0) {
        this.doc.setDrawColor(...RULE_SOFT);
        this.doc.setLineWidth(0.9);
        this.doc.line(x, this.y + 1, x, this.y + h - 1);
      }
      this.label(c.k, x + 10, this.y + 8, { size: 6.6, track: 0.9 });
      this.text(c.v, x + 10, this.y + 18, 11.5, "bold", INK, {
        maxWidth: cw - 20,
      });
      if (c.n) {
        this.text(c.n, x + 10, this.y + 32, 7.4, "normal", MUTED, {
          maxWidth: cw - 20,
        });
      }
    });
    this.y += h + GAP_SECTION;
  }

  /** Small tally line under the room photos. */
  flags(items: Array<{ n: string; label: string }>): void {
    this.need(16);
    let x = MARGIN;
    for (const it of items) {
      this.font(8, "bold", INK);
      const nw = this.doc.getTextWidth(it.n);
      this.text(it.n, x, this.y, 8, "bold", INK);
      const lw = this.label(it.label, x + nw + 4, this.y + 0.8, {
        size: 6.8,
        color: MUTED,
        track: 0.6,
      });
      x += nw + 4 + lw + 18;
    }
    this.y += 16 + GAP_BLOCK;
  }

  /**
   * Photo plates.
   *
   * `need()` is called per ROW, before any of that row's images are placed,
   * so a break happens between rows and never inside one.
   */
  async photoGrid(
    plates: Array<{ path: string; ref: string; note: string }>,
    cols: number,
    aspect: number
  ): Promise<void> {
    if (!plates.length) {
      this.text("—", MARGIN, this.y, 10, "normal", FAINT);
      this.y += 18;
      return;
    }
    const colW = (CONTENT_W - GUTTER * (cols - 1)) / cols;
    const imgH = colW / aspect;
    const capH = 13;

    for (let i = 0; i < plates.length; i += cols) {
      this.need(imgH + capH);
      const rowTop = this.y;
      const row = plates.slice(i, i + cols);
      for (let c = 0; c < row.length; c++) {
        const x = MARGIN + c * (colW + GUTTER);
        const plate = row[c];
        const dataUrl = await pathToDataUrl(plate.path);
        let placed = false;
        if (dataUrl) {
          try {
            this.doc.addImage(
              dataUrl,
              getImageFormat(dataUrl),
              x,
              rowTop,
              colW,
              imgH
            );
            placed = true;
          } catch {
            placed = false;
          }
        }
        if (!placed) {
          this.box(x, rowTop, colW, imgH, RULE_SOFT, RULE);
        } else {
          this.box(x, rowTop, colW, imgH, null, RULE);
        }
        this.label(plate.ref, x, rowTop + imgH + 3.5, {
          size: 6.6,
          color: INK,
          weight: "bold",
          track: 0.6,
        });
        if (plate.note) {
          this.label(plate.note, x + colW, rowTop + imgH + 3.5, {
            size: 6.6,
            align: "right",
            track: 0.6,
          });
        }
      }
      this.y = rowTop + imgH + capH + GAP_ROW;
    }
    this.y += GAP_BLOCK - GAP_ROW;
  }

  /** Inventory table. Columns are wide enough not to hyphenate a condition. */
  inventoryTable(
    rows: Array<{
      label: string;
      meta: string | null;
      condition: ItemCondition | null;
      note: string;
      photoRef: string;
    }>
  ): void {
    const cols = [
      { x: MARGIN, w: CONTENT_W * 0.29 },
      { x: MARGIN + CONTENT_W * 0.29, w: CONTENT_W * 0.21 },
      { x: MARGIN + CONTENT_W * 0.5, w: CONTENT_W * 0.36 },
      { x: MARGIN + CONTENT_W * 0.86, w: CONTENT_W * 0.14 },
    ];

    const head = () => {
      this.need(30);
      this.label(this.t("Item", "Gegenstand"), cols[0].x, this.y, {
        size: 6.6,
      });
      this.label(this.t("Condition", "Zustand"), cols[1].x, this.y, {
        size: 6.6,
      });
      this.label(this.t("Notes", "Bemerkungen"), cols[2].x, this.y, {
        size: 6.6,
      });
      this.label(this.t("Photo", "Foto"), cols[3].x, this.y, { size: 6.6 });
      this.rule(this.y + 10, MARGIN, RIGHT, INK);
      this.y += 15;
    };
    head();

    for (const r of rows) {
      const noteH = r.note ? this.measure(r.note, cols[2].w - 8, 8.4) : 0;
      const rowH = Math.max(r.meta ? 24 : 15, noteH + 6, 20);
      // A break re-prints the header: a headerless table continuation is not
      // readable, and this is where the old version simply ran off the page.
      if (this.y + rowH > CONTENT_BOTTOM) {
        this.newPage();
        head();
      }
      const top = this.y;
      this.text(r.label, cols[0].x, top, 9, "bold", INK, {
        maxWidth: cols[0].w - 8,
      });
      if (r.meta) {
        this.text(r.meta, cols[0].x, top + 11, 8.4, "normal", MUTED, {
          maxWidth: cols[0].w - 8,
        });
      }
      if (r.condition) {
        const c = COND_COLOR[r.condition];
        this.chip(
          COND_LABEL[r.condition][this.lang],
          cols[1].x,
          top - 1,
          c.fg,
          c.bg
        );
      } else {
        this.text("—", cols[1].x, top, 8.4, "normal", FAINT);
      }
      if (r.note) {
        this.paragraph(r.note, cols[2].x, top, cols[2].w - 8, 8.4, INK);
      } else {
        this.text(
          this.t("none", "keine"),
          cols[2].x,
          top,
          8.4,
          "normal",
          FAINT
        );
      }
      this.label(r.photoRef, cols[3].x, top + 0.5, {
        size: 7,
        color: r.photoRef === "—" ? FAINT : MUTED,
        track: 0.6,
      });

      this.y = top + rowH;
      this.rule(this.y - 4, MARGIN, RIGHT, RULE_SOFT);
    }
    this.y += GAP_BLOCK;
  }

  defects(
    rows: Array<{ area: string; scope: string; text: string; meta: string[] }>,
    severityColor: (i: number) => RGB
  ): void {
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i];
      const textH = this.measure(r.text, CONTENT_W * 0.52, 8.8);
      const h = Math.max(textH + 14, 34);
      this.need(h + GAP_ROW);
      const top = this.y;
      this.box(MARGIN, top, CONTENT_W, h, WHITE, RULE_SOFT);
      this.doc.setFillColor(...severityColor(i));
      this.doc.rect(MARGIN, top, 2.4, h, "F");

      this.text(r.area, MARGIN + 10, top + 7, 9, "bold", INK);
      this.label(r.scope, MARGIN + 10, top + 19, { size: 7, track: 0.6 });
      this.paragraph(r.text, MARGIN + 100, top + 7, CONTENT_W * 0.52, 8.8, INK);
      r.meta.forEach((m, j) => {
        this.label(m, RIGHT - 10, top + 7 + j * 10, {
          size: 6.8,
          align: "right",
          color: MUTED,
          track: 0.6,
        });
      });
      this.y = top + h + GAP_ROW;
    }
    this.y += GAP_BLOCK - GAP_ROW;
  }

  /** A free-text note, in its own quiet box. */
  note(body: string): void {
    const h = this.measure(body, CONTENT_W - 24, 8.8) + 18;
    this.need(h);
    const top = this.y;
    this.box(MARGIN, top, CONTENT_W, h, WHITE, RULE_SOFT);
    this.paragraph(body, MARGIN + 12, top + 9, CONTENT_W - 24, 8.8, INK);
    this.y = top + h + GAP_BLOCK;
  }

  /**
   * What this document actually is, when it is not a tenant walkthrough.
   *
   * `lib/protocol.ts` states the rule this satisfies: every reader that shows
   * a protocol as evidence of a walkthrough must check `isAutoSubmitted` /
   * `isAdminRecorded` before claiming a tenant signed it. A signature box
   * labelled "Mieter:in" over an empty rule would claim exactly that.
   */
  provenanceBox(heading: string, lines: string[]): void {
    const bodyH = lines.reduce(
      (h, l) => h + (l ? this.measure(l, CONTENT_W - 28, 8.6) : 6),
      0
    );
    const h = bodyH + 34;
    this.need(h);
    const top = this.y;
    this.box(MARGIN, top, CONTENT_W, h, WHITE, RULE);
    this.doc.setFillColor(...WEAR);
    this.doc.rect(MARGIN, top, 2.6, h, "F");
    this.text(heading, MARGIN + 12, top + 10, 9.6, "bold", INK);
    let cy = top + 26;
    for (const line of lines) {
      if (!line) {
        cy += 6;
        continue;
      }
      cy += this.paragraph(line, MARGIN + 12, cy, CONTENT_W - 28, 8.6, INK);
    }
    this.y = top + h + GAP_BLOCK;
  }

  signature(o: {
    signatureDataUrl: string | null;
    tenantLine: string;
    tenantMeta: string;
    systemLine: string;
    systemMeta: string;
  }): void {
    const h = 96;
    this.need(h);
    const top = this.y;
    const colW = (CONTENT_W - 16) / 2;

    // Tenant — the drawn signature.
    this.box(MARGIN, top, colW, h, WHITE, RULE);
    this.label(this.t("Tenant", "Mieter:in"), MARGIN + 11, top + 9, {
      size: 6.8,
      track: 0.9,
    });
    if (o.signatureDataUrl) {
      try {
        this.doc.addImage(
          o.signatureDataUrl,
          getImageFormat(o.signatureDataUrl),
          MARGIN + 11,
          top + 22,
          Math.min(colW - 22, 150),
          34
        );
      } catch {
        // best effort — the lines below still identify the signer
      }
    }
    this.rule(top + 62, MARGIN + 11, MARGIN + colW - 11);
    this.text(o.tenantLine, MARGIN + 11, top + 67, 8.6, "bold", INK);
    this.label(o.tenantMeta, MARGIN + 11, top + 79, {
      size: 7.2,
      color: MUTED,
      track: 0.4,
    });

    // CoLiviq — the system's own confirmation, marked as such.
    const rx = MARGIN + colW + 16;
    this.box(rx, top, colW, h, WHITE, RULE);
    this.label(
      this.t(
        "Coliviq GmbH · system record",
        "Coliviq GmbH · Systembestätigung"
      ),
      rx + 11,
      top + 9,
      { size: 6.8, track: 0.9 }
    );
    const mark = coliviqMarkMetrics(12);
    this.doc.addImage(
      COLIVIQ_MARK_PNG_DATA_URL,
      "PNG",
      rx + 11,
      top + 24,
      mark.width,
      mark.height
    );
    this.rule(top + 62, rx + 11, rx + colW - 11);
    this.text(o.systemLine, rx + 11, top + 67, 8.6, "bold", INK, {
      maxWidth: colW - 22,
    });
    this.label(o.systemMeta, rx + 11, top + 79, {
      size: 7.2,
      color: MUTED,
      track: 0.4,
    });

    this.y = top + h + GAP_BLOCK;
  }

  /** Footer + bottom bar on every page, once the page count is known. */
  finish(legalRef: string): void {
    const total = this.doc.getNumberOfPages();
    for (let i = 1; i <= total; i++) {
      this.doc.setPage(i);
      const fy = A4.h - BOT_BAR_H - 26;
      this.rule(fy);
      this.text(COMPANY.legal, MARGIN, fy + 7, 6.6, "normal", FAINT);
      this.text(
        `${this.t("Page", "Seite")} ${i} / ${total} · ${legalRef}`,
        RIGHT,
        fy + 7,
        6.6,
        "normal",
        MUTED,
        { align: "right" }
      );
      this.doc.setFillColor(...BAR);
      this.doc.rect(0, A4.h - BOT_BAR_H, A4.w, BOT_BAR_H, "F");
    }
  }
}

/* ── the document ────────────────────────────────────────────────────────── */

export async function generateProtocolPdf(
  input: ProtocolPdfInput
): Promise<Blob> {
  const lang = input.language === "de" ? "de" : "en";
  const t = (en: string, de: string) => (lang === "de" ? de : en);
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const P = new Painter(doc, lang);

  const { protocol, booking, tenant } = input;
  const isMoveIn = protocol.kind === "move_in";
  const submittedAt = protocol.submitted_at ?? new Date().toISOString();
  const dateStr = formatDateDE(submittedAt);
  const timeStr = formatTimeDE(submittedAt);
  const roomName = booking?.room_number
    ? `${t("Room", "Zimmer")} ${booking.room_number}`
    : "—";

  /* ── page 1: letterhead, identity, overview ───────────────────────────── */

  P.letterhead();
  P.setContinuationMeta(
    [
      isMoveIn
        ? t("Move-in protocol", "Einzugsprotokoll")
        : t("Move-out protocol", "Auszugsprotokoll"),
      protocol.ref,
      [roomName, booking?.property_name].filter(Boolean).join(", "),
    ]
      .filter(Boolean)
      .join(" · ")
  );

  /**
   * How this protocol came to exist. Everything the document CLAIMS follows
   * from this — the status, the subtitle and the closing block — because a
   * deemed-accepted room and an admin-filed record are not tenant walkthroughs
   * and must not be presented as one.
   */
  const provenance: "tenant" | "admin" | "auto" = isAutoSubmitted(protocol)
    ? "auto"
    : isAdminRecorded(protocol)
      ? "admin"
      : "tenant";

  const statusColor =
    provenance === "tenant" && protocol.status === "submitted"
      ? { fg: GOOD, bg: GOOD_BG }
      : { fg: WEAR, bg: WEAR_BG };
  const statusLabel =
    provenance === "auto"
      ? t("Auto-accepted", "Automatisch angenommen")
      : provenance === "admin"
        ? t("Recorded by coliviq", "Von coliviq erfasst")
        : protocol.status === "submitted"
          ? t("Completed", "Vollständig eingereicht")
          : protocol.status === "draft"
            ? t("Draft", "Entwurf")
            : protocol.status;

  const subtitleFor = () => {
    if (provenance === "auto") {
      return "Frist abgelaufen, gilt als angenommen";
    }
    if (provenance === "admin") {
      const who = protocol.recorded_by_name ?? "coliviq";
      return `Erfasst von ${who}, keine Unterschrift`;
    }
    return "Vom Mieter eingereicht";
  };

  P.titleBlock({
    kicker: t(
      "Übergabeprotokoll · Handover protocol",
      "Übergabeprotokoll · Handover protocol"
    ),
    title: isMoveIn ? "Einzugsprotokoll" : "Auszugsprotokoll",
    sub: subtitleFor(),
    refLabel: "Referenz / Reference",
    ref: protocol.ref,
    status: statusLabel,
    statusColor,
    when: `${dateStr}${timeStr ? `, ${timeStr} ${t("", "Uhr")}`.trimEnd() : ""}`,
  });

  P.sectionHead("Übersicht", "Overview");
  P.twoColumnFacts(
    [
      {
        label: t("Tenant", "Mieter:in"),
        value: tenant.name || tenant.email || "—",
      },
      { label: t("Email", "E-Mail"), value: tenant.email ?? "—" },
      {
        label: t("Operation", "Vorgang"),
        value: isMoveIn ? t("Move-in", "Einzug") : t("Move-out", "Auszug"),
        sub: isMoveIn ? t("Einzug", "Move-in") : t("Auszug", "Move-out"),
      },
    ],
    [
      { label: t("Property", "Objekt"), value: booking?.property_name ?? "—" },
      { label: t("Room", "Zimmer"), value: roomName },
      {
        label: t("Address", "Adresse"),
        value: booking?.address ?? "—",
      },
      { label: t("Representor", "Vertreter"), value: COMPANY.name },
    ]
  );

  const cells: Cell[] = [];
  if (booking?.move_in_date) {
    cells.push({
      k: "Einzug / Move-in",
      v: formatDateDE(booking.move_in_date),
    });
  }
  if (booking?.move_out_date) {
    cells.push({
      k: "Auszug / Move-out",
      v: formatDateDE(booking.move_out_date),
    });
  }
  if (typeof booking?.deposit_amount === "number") {
    cells.push({
      k: "Kaution / Deposit",
      v: formatEuro(booking.deposit_amount),
    });
  }
  // "Eingereicht" on a protocol nobody submitted is the same false claim the
  // status pill used to make — the cell names the event that actually occurred.
  const closingEvent =
    provenance === "auto"
      ? {
          k: t("Window closed", "Frist abgelaufen"),
          iso: protocol.auto_submitted_at ?? submittedAt,
        }
      : provenance === "admin"
        ? {
            k: t("Recorded", "Erfasst"),
            iso: protocol.recorded_at ?? submittedAt,
          }
        : { k: t("Submitted", "Eingereicht"), iso: submittedAt };
  const closingTime = formatTimeDE(closingEvent.iso);
  cells.push({
    k: closingEvent.k,
    v: formatDateDE(closingEvent.iso),
    n: closingTime ? `${closingTime} ${t("", "Uhr")}`.trimEnd() : undefined,
  });
  P.strip(cells);

  /* ── room photos ──────────────────────────────────────────────────────── */

  const roomPhotos = input.photos
    .filter((p) => p.category === "room")
    .sort((a, b) => a.ordering - b.ordering);

  P.sectionHead(
    "Fotos vom Zimmer",
    "Room photos",
    Math.round(((CONTENT_W - GUTTER * 2) / 3) * 0.75) + 26
  );
  await P.photoGrid(
    roomPhotos.map((p, i) => ({
      path: p.storage_path,
      ref: `${PHOTO_PREFIX.room}-${String(i + 1).padStart(2, "0")}`,
      note: formatTimeDE(p.created_at),
    })),
    3,
    4 / 3
  );
  P.flags([
    {
      n: String(roomPhotos.length),
      label: t("room photos", "Fotos vom Zimmer"),
    },
    {
      n: String(input.defects.length),
      label: t("defects noted", "Mängel notiert"),
    },
    {
      n: String(input.photos.length),
      label: t("photos in total", "Fotos insgesamt"),
    },
  ]);

  // Include key evidence in the same document. Historical protocols without it
  // retain their existing layout; no new completion or proof requirement is inferred.
  const keyPhotos = input.photos
    .filter((p) => p.category === "keys")
    .sort((a, b) => a.ordering - b.ordering);
  if (keyPhotos.length > 0) {
    const pickup = input.protocol.kind === "move_in";
    P.sectionHead(
      pickup
        ? "Nachweis der Schlüsselabholung"
        : "Nachweis der Schlüsselrückgabe",
      pickup ? "Key pickup proof" : "Key return proof",
      Math.round(((CONTENT_W - GUTTER * 2) / 3) * 0.75) + 26
    );
    await P.photoGrid(
      keyPhotos.map((p, i) => ({
        path: p.storage_path,
        ref: `${PHOTO_PREFIX.keys}-${String(i + 1).padStart(2, "0")}`,
        note: "",
      })),
      3,
      4 / 3
    );
  }

  /* ── inventory ────────────────────────────────────────────────────────── */

  const inventory = [...input.inventory].sort(
    (a, b) => a.ordering - b.ordering
  );
  const photoRefFor = new Map<string, string>();
  let invPhotoN = 0;
  for (const it of inventory) {
    if (it.photo_storage_path) {
      invPhotoN += 1;
      photoRefFor.set(
        it.id,
        `${INVENTORY_PREFIX}-${String(invPhotoN).padStart(2, "0")}`
      );
    }
  }

  if (inventory.length) {
    P.sectionHead("Inventar", "Inventory", 50);
    P.inventoryTable(
      inventory.map((it) => ({
        label: it.label,
        meta: it.meta,
        condition: it.condition,
        note: it.defect_description
          ? `${it.defect_description}${
              it.defect_severity
                ? ` (${SEV_LABEL[it.defect_severity][lang]})`
                : ""
            }`
          : "",
        photoRef: photoRefFor.get(it.id) ?? "—",
      }))
    );

    const withPhotos = inventory.filter((it) => !!it.photo_storage_path);
    if (withPhotos.length) {
      P.sectionHead(
        "Inventar-Fotos",
        "Inventory photos",
        Math.round(((CONTENT_W - GUTTER * 2) / 3) * 0.75) + 26
      );
      await P.photoGrid(
        withPhotos.map((it) => ({
          path: it.photo_storage_path!,
          ref: photoRefFor.get(it.id) ?? "—",
          note: it.label,
        })),
        3,
        4 / 3
      );
    }
  }

  /* ── shared areas ─────────────────────────────────────────────────────── */

  const areaRefFor = new Map<string, string>();
  for (const area of COMMON_AREAS) {
    const areaPhotos = input.photos
      .filter((p) => p.category === area.id)
      .sort((a, b) => a.ordering - b.ordering);
    if (!areaPhotos.length) continue;

    P.sectionHead(
      `Gemeinschaftsbereich: ${AREA_LABEL[area.id].de}`,
      `Shared area: ${AREA_LABEL[area.id].en}`,
      Math.round(((CONTENT_W - GUTTER) / 2) * 0.5625) + 26
    );
    await P.photoGrid(
      areaPhotos.map((p, i) => {
        const ref = `${PHOTO_PREFIX[area.id]}-${String(i + 1).padStart(2, "0")}`;
        if (i === 0) areaRefFor.set(area.id, ref);
        return {
          path: p.storage_path,
          ref,
          note: formatTimeDE(p.created_at),
        };
      }),
      2,
      16 / 9
    );
  }

  /* ── defects ──────────────────────────────────────────────────────────── */

  if (input.defects.length) {
    P.sectionHead("Gemeldete Mängel", "Reported defects", 40);
    const sevColor: Record<Severity, RGB> = {
      low: WEAR,
      medium: WEAR,
      high: BAD,
    };
    P.defects(
      input.defects.map((d) => ({
        area: AREA_LABEL[d.area][lang],
        scope: t("shared", "Gemeinschaft"),
        text: d.description,
        meta: [
          SEV_LABEL[d.severity][lang],
          d.photo_storage_path
            ? `${t("photo", "Foto")} ${areaRefFor.get(d.area) ?? "—"}`
            : "",
        ].filter(Boolean),
      })),
      (i) => sevColor[input.defects[i].severity]
    );
  }

  /* ── signature ────────────────────────────────────────────────────────── */

  if (protocol.admin_note) {
    P.sectionHead("Anmerkung coliviq", "coliviq note", 40);
    P.note(protocol.admin_note);
  }

  if (provenance === "tenant") {
    P.sectionHead("Unterschrift", "Signature", 96);
    P.signature({
      signatureDataUrl: protocol.signature_data,
      tenantLine: tenant.name || tenant.email || "—",
      tenantMeta: `${dateStr}${timeStr ? `, ${timeStr}` : ""} · ${t(
        "captured digitally",
        "digital erfasst"
      )}`,
      systemLine: `${t("Handover protocol", "Übergabeprotokoll")} ${protocol.ref}`,
      systemMeta: `${t("Filed", "Abgelegt")} ${dateStr}${
        timeStr ? `, ${timeStr}` : ""
      }`,
    });
  } else {
    P.sectionHead("Herkunft dieses Dokuments", "Provenance", 90);
    const kindDe = isMoveIn ? "Einzugsprotokoll" : "Auszugsprotokoll";
    const kindEn = isMoveIn ? "move-in" : "move-out";
    if (provenance === "auto") {
      const closedOn = formatDateDE(protocol.auto_submitted_at ?? submittedAt);
      P.provenanceBox(
        t("No walkthrough was recorded", "Es wurde keine Begehung erfasst"),
        [
          `Die Frist für dieses ${kindDe} ist am ${closedOn} abgelaufen, ohne dass eine ` +
            `Begehung eingereicht wurde. Das Zimmer gilt damit gemäß Mietvertrag als angenommen.`,
          `Es handelt sich NICHT um eine von der Mieterin / dem Mieter durchgeführte und ` +
            `unterzeichnete Begehung. Eine Unterschrift liegt nicht vor.`,
          "",
          `The window for this ${kindEn} protocol closed on ${closedOn} with no walkthrough ` +
            `submitted. The room is therefore deemed accepted under the rental contract.`,
          `It is NOT a walkthrough completed and signed by the tenant, and carries no ` +
            `tenant signature.`,
        ]
      );
    } else {
      const who = protocol.recorded_by_name ?? "coliviq";
      const on = formatDateDE(protocol.recorded_at ?? submittedAt);
      P.provenanceBox(
        t(
          "Recorded on the tenant's behalf",
          "Im Namen der Mieterin / des Mieters erfasst"
        ),
        [
          `Dieses ${kindDe} wurde am ${on} von ${who} (coliviq) im Namen der Mieterin / ` +
            `des Mieters erfasst.`,
          `Es handelt sich NICHT um eine von der Mieterin / dem Mieter in der App ` +
            `durchgeführte und unterzeichnete Begehung. Eine Unterschrift liegt nicht vor.`,
          "",
          `This ${kindEn} protocol was recorded on ${on} by ${who} (coliviq) on the ` +
            `tenant's behalf.`,
          `It is NOT a walkthrough completed and signed by the tenant, and carries no ` +
            `tenant signature.`,
        ]
      );
    }
  }

  P.finish(protocol.ref);
  return doc.output("blob");
}

/** Saves the PDF to the user's device with a sensible filename. */
export function downloadProtocolPdf(blob: Blob, ref: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${ref}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
